class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.refreshCartContext();
  }

  async refreshCartContext() {
    const callId = (this.cartContextCallId || 0) + 1;
    this.cartContextCallId = callId;
    try {
      const response = await fetch(routes.cart_url + '.json');
      const cart = await response.json();
      if (this.cartContextCallId !== callId || !this.isConnected) return;

      this.renderLegamiShippingProgress(cart);
      this.renderLegamiSuggestions(cart);

      const { CartViewEvent } = window.StandardEvents || {};
      if (!CartViewEvent || !cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      if (this.cartContextCallId !== callId || !this.isConnected) return;
      this.renderLegamiShippingProgress(null);
      this.renderLegamiSuggestions(null);
    }
  }

  renderLegamiShippingProgress(cart) {
    let banner = this.querySelector('[data-hp-legami-shipping-progress]');
    if (!banner) {
      banner = document.createElement('div');
      banner.setAttribute('data-hp-legami-shipping-progress', '');
      banner.setAttribute('role', 'status');
      banner.style.cssText = 'margin:14px 0;padding:12px 14px;border:1px solid #c8dfd2;border-radius:10px;background:#f0f8f3;color:#111;font-size:13px;line-height:1.45;text-align:left;';
      this.querySelector('.cart-notification__links')?.before(banner);
    }
    banner.hidden = true;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length || cart.currency !== 'EUR' || this.dataset.country !== 'IT') return;
    const copy = this.cartCopy();
    const eligibleProductIds = new Set(
      Array.isArray(copy.eligibleProductIds) ? copy.eligibleProductIds.map(String) : []
    );
    const eligible = items.every((item) =>
      eligibleProductIds.has(String(item.product_id)) && !item.gift_card
    );
    if (!eligible) return;
    const total = Number(cart.total_price) || 0;
    const format = new Intl.NumberFormat(this.dataset.locale || 'it-IT', { style: 'currency', currency: 'EUR' });
    const heading = document.createElement('strong');
    heading.textContent = total >= 4900 ? copy.reached : copy.remaining.replace('[amount]', format.format((4900 - total) / 100));
    const terms = document.createElement('div');
    terms.style.marginTop = '4px';
    terms.textContent = copy.terms;
    banner.replaceChildren(heading, terms);
    banner.hidden = false;
  }

  cartCopy() {
    if (!this.hpCopy) {
      this.hpCopy = JSON.parse(this.querySelector('[data-hp-cart-copy]')?.textContent || '{}');
    }
    return this.hpCopy;
  }

  async renderLegamiSuggestions(cart) {
    const section = this.querySelector('[data-hp-cart-suggestions]');
    const container = section?.querySelector('.hp-cart-suggestions__items');
    if (!section || !container) return;
    const callId = (this.suggestionsCallId || 0) + 1;
    this.suggestionsCallId = callId;
    section.hidden = true;
    container.replaceChildren();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const selected = items.find((line) => line.key === this.cartItemKey) || items[0];
    if (!selected || String(selected.vendor || '').trim().toLowerCase() !== 'legami') return;
    const cartIds = new Set(items.map((line) => String(line.product_id)));
    cartIds.add(String(selected.product_id));
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root + 'recommendations/products.json', location.origin);
    url.searchParams.set('product_id', selected.product_id);
    url.searchParams.set('intent', 'complementary');
    url.searchParams.set('limit', '10');
    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      if (this.suggestionsCallId !== callId || !this.isConnected) return;
      const seen = new Set(cartIds);
      const recommended = [];
      for (const product of data.products || []) {
        const variants = (product.variants || []).filter((variant) => variant.available && Number(variant.price) > 0);
        if (seen.has(String(product.id)) || !product.available || !product.featured_image ||
            product.gift_card || product.requires_selling_plan || !variants.length ||
            String(product.vendor || '').trim().toLowerCase() !== 'legami') continue;
        seen.add(String(product.id));
        recommended.push({ product, variants });
        if (recommended.length === 3) break;
      }
      if (!recommended.length) return;
      const copy = this.cartCopy();
      const format = new Intl.NumberFormat(this.dataset.locale || 'it-IT', { style: 'currency', currency: cart.currency || 'EUR' });
      for (const { product, variants } of recommended) {
        const card = document.createElement('div');
        card.className = 'hp-cart-suggestion';
        card.dataset.hpSuggestionId = String(product.id);
        const productUrl = typeof product.url === 'string' && product.url.startsWith('/') && !product.url.startsWith('//')
          ? product.url : root + 'products/' + encodeURIComponent(product.handle);
        const imageLink = document.createElement('a');
        imageLink.href = productUrl;
        const img = document.createElement('img');
        img.src = variants[0].featured_image?.src || product.featured_image;
        img.alt = product.title;
        img.loading = 'lazy';
        img.width = 60;
        img.height = 60;
        imageLink.append(img);
        const details = document.createElement('div');
        details.className = 'hp-cart-suggestion__details';
        const titleLink = document.createElement('a');
        titleLink.href = productUrl;
        const title = document.createElement('strong');
        title.textContent = product.title.replace(/^Legami\s+/i, '');
        titleLink.append(title);
        const price = document.createElement('span');
        price.className = 'hp-cart-suggestion__price';
        price.textContent = format.format(Number(variants[0].price) / 100);
        const form = document.createElement('form');
        form.action = routes.cart_add_url;
        form.method = 'post';
        let variantControl;
        if (variants.length > 1) {
          variantControl = document.createElement('select');
          variantControl.required = true;
          variantControl.setAttribute('aria-label', copy.choose + ': ' + product.title);
          const placeholder = new Option(copy.choose, '', true, true);
          placeholder.disabled = true;
          variantControl.append(placeholder);
          for (const variant of variants) {
            variantControl.append(new Option(variant.title + ' · ' + format.format(Number(variant.price) / 100), String(variant.id)));
          }
          variantControl.addEventListener('change', () => {
            const variant = variants.find((entry) => String(entry.id) === variantControl.value);
            if (!variant) return;
            price.textContent = format.format(Number(variant.price) / 100);
            img.src = variant.featured_image?.src || product.featured_image;
            img.alt = product.title + ' - ' + variant.title;
          });
        } else {
          variantControl = document.createElement('input');
          variantControl.type = 'hidden';
          variantControl.value = String(variants[0].id);
        }
        variantControl.name = 'id';
        const button = document.createElement('button');
        button.type = 'submit';
        button.textContent = copy.add;
        button.setAttribute('aria-label', copy.addLabel.replace('[product]', product.title));
        const error = document.createElement('p');
        error.setAttribute('role', 'alert');
        error.hidden = true;
        form.append(variantControl, button, error);
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (this.hpAdding || !variantControl.value || !form.reportValidity()) return;
          this.hpAdding = true;
          container.querySelectorAll('button[type="submit"]').forEach((item) => { item.disabled = true; });
          button.textContent = copy.adding;
          error.hidden = true;
          try {
            const result = await fetch(routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: variantControl.value, quantity: 1,
                sections: this.getSectionsToRender().map((item) => item.id),
                sections_url: window.location.pathname })
            });
            const added = await result.json();
            if (!result.ok || added.status) throw new Error(added.description || copy.error);
            const sectionsValid = this.getSectionsToRender().every((item) => typeof added.sections?.[item.id] === 'string');
            if (sectionsValid) {
              this.renderContents(added);
            } else {
              window.location.assign(routes.cart_url);
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hp-cart-suggestion', productVariantId: variantControl.value, cartData: added });
            }
          } catch (failure) {
            error.textContent = failure.message || copy.error;
            error.hidden = false;
          } finally {
            this.hpAdding = false;
            if (button.isConnected) button.textContent = copy.add;
            container.querySelectorAll('button[type="submit"]').forEach((item) => { item.disabled = false; });
          }
        });
        details.append(titleLink, price, form);
        card.append(imageLink, details);
        container.append(card);
      }
      section.hidden = false;
    } catch (_) {
      if (this.suggestionsCallId === callId) section.hidden = true;
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: '[id="cart-notification-product-' + this.cartItemKey + '"]',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);

